import Link from 'found/Link';
import { DateTime } from 'luxon';
import PropTypes from 'prop-types';
import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useIntl } from 'react-intl';
import { createFragmentContainer, graphql } from 'react-relay';
import {
  Card, CardHeader, Col, Row, Table,
} from 'reactstrap';

import BadgeActive from 'src/components/Badge/BadgeActive';
import BadgeActiveTimeRange from 'src/components/Badge/BadgeActiveTimeRange';
import Loading from 'src/components/Loading';
import UUID from 'src/components/UUID';
import Breadcrumbs from 'src/enosikit/components/Breadcrumbs';
import displayUserName from 'src/util/i18n/decorators/displayUserName';

/**
 * Description
 * @param {object} props
 * @returns {React.ReactElement} - AdminPropertyUserShow component
 */
function AdminPropertyUserShow(props) {
  if (!props) {
    return <Loading />;
  }

  const { propertyUser } = props;
  const {
    uuid, externalIdentifier, createdAt, updatedAt,
    active, property, user,
  } = propertyUser;
  const { start, finish } = active;
  const {
    uuid: propertyId, title: propertyTitle, active: propertyActive,
    externalIdentifier: propertyExternalIdentifier,
  } = property;
  const {
    uuid: userId, externalIdentifier: userExternalIdentifier,
    email: userEmail, active: userActive,
  } = user;

  const intl = useIntl();

  // Page title and breadcrumbs
  const pageTitle = intl.formatMessage(
    { id: 'admin.admin_property_user.admin_property_user_show.page.title', defaultMessage: 'Enosi Admin - Property User - {uuid}' },
    { uuid },
  );
  const breadcrumbAdmin = intl.formatMessage(
    { id: 'admin.admin_property_user.admin_property_user_show.breadcrumbs.admin.label', defaultMessage: 'Admin' },
  );
  const breadcrumbPropertyUsers = intl.formatMessage(
    { id: 'admin.admin_property_user.admin_property_user_show.breadcrumbs.property_users.label', defaultMessage: 'Property Users' },
  );
  const breadcrumbPropertyUser = intl.formatMessage(
    { id: 'admin.admin_property_user.admin_property_user_show.breadcrumbs.property_user.label', defaultMessage: 'Property User: {uuid}' },
    { uuid },
  );

  // Card headers
  const propertyUserHeader = intl.formatMessage(
    { id: 'admin.admin_property_user.admin_property_user_show.card.property_user.header', defaultMessage: 'Property user details' },
  );
  const propertyHeader = intl.formatMessage(
    { id: 'admin.admin_property_user.admin_property_user_show.card.property.header', defaultMessage: 'Property' },
  );
  const userHeader = intl.formatMessage(
    { id: 'admin.admin_property_user.admin_property_user_show.card.user.header', defaultMessage: 'User' },
  );

  // Table headers
  const tableActiveStatusHeader = intl.formatMessage(
    { id: 'admin.admin_property_user.admin_property_user_show.table.active_status.header', defaultMessage: 'Active' },
  );
  const tableIdHeader = intl.formatMessage(
    { id: 'admin.admin_property_user.admin_property_user_show.table.id.header', defaultMessage: 'ID' },
  );
  const tableExternalIdentifierTitle = intl.formatMessage(
    { id: 'admin.admin_property_user.admin_property_user_show.table.external_identifier.title', defaultMessage: 'External identifier' },
  );
  const tableExternalIdentifierLabel = intl.formatMessage(
    { id: 'admin.admin_property_user.admin_property_user_show.table.external_identifier.label', defaultMessage: 'Ext ID' },
  );
  const tableStartHeader = intl.formatMessage(
    { id: 'admin.admin_property_user.admin_property_user_show.table.start.header', defaultMessage: 'Start' },
  );
  const tableFinishHeader = intl.formatMessage(
    { id: 'admin.admin_property_user.admin_property_user_show.table.finish.header', defaultMessage: 'Finish' },
  );
  const tableCreatedAtHeader = intl.formatMessage(
    { id: 'admin.admin_property_user.admin_property_user_show.table.created_at.header', defaultMessage: 'Created at' },
  );
  const tableUpdatedAtHeader = intl.formatMessage(
    { id: 'admin.admin_property_user.admin_property_user_show.table.updated_at.header', defaultMessage: 'Updated at' },
  );
  const tableTitleHeader = intl.formatMessage(
    { id: 'admin.admin_property_user.admin_property_user_show.table.title.header', defaultMessage: 'Title' },
  );
  const tableUsernameHeader = intl.formatMessage(
    { id: 'admin.admin_property_user.admin_property_user_show.table.username.header', defaultMessage: 'Username' },
  );
  const tableEmailHeader = intl.formatMessage(
    { id: 'admin.admin_property_user.admin_property_user_show.table.email.header', defaultMessage: 'Email' },
  );

  return (
    <>
      <Helmet>
        <meta charSet="utf-8" />
        <title>{pageTitle}</title>
      </Helmet>

      <Breadcrumbs
        breadcrumbs={[
          { name: breadcrumbAdmin, path: '/admin' },
          { name: breadcrumbPropertyUsers, path: '/admin/property-users' },
          { name: breadcrumbPropertyUser },
        ]}
      />

      <h1>{uuid}</h1>

      <Row className="clear-both">
        <Col xs={12} sm={6} md={4}>
          <Card className="mt-4 mb-4">
            <CardHeader>
              {propertyUserHeader}
            </CardHeader>
            <Table responsive>
              <tbody>
                <tr>
                  <th scope="row" className="text-right">{tableActiveStatusHeader}</th>
                  <td>
                    <BadgeActive start={start} finish={finish} />
                  </td>
                </tr>
                <tr>
                  <th scope="row" className="text-right">{tableIdHeader}</th>
                  <td>
                    <UUID uuid={uuid} />
                  </td>
                </tr>
                <tr>
                  <th scope="row" className="text-right">
                    <abbr title={tableExternalIdentifierTitle}>{tableExternalIdentifierLabel}</abbr>
                  </th>
                  <td>{externalIdentifier}</td>
                </tr>
                <tr>
                  <th scope="row" className="text-right">{tableStartHeader}</th>
                  <td>
                    {start && DateTime.fromSeconds(start).toLocaleString(DateTime.DATETIME_FULL)}
                  </td>
                </tr>
                <tr>
                  <th scope="row" className="text-right">{tableFinishHeader}</th>
                  <td>
                    {finish && DateTime.fromSeconds(finish).toLocaleString(DateTime.DATETIME_FULL)}
                  </td>
                </tr>
                <tr>
                  <th scope="row" className="text-right">{tableCreatedAtHeader}</th>
                  <td>
                    {createdAt
                      && DateTime.fromSeconds(createdAt).toLocaleString(DateTime.DATETIME_FULL)}
                  </td>
                </tr>
                <tr>
                  <th scope="row" className="text-right">{tableUpdatedAtHeader}</th>
                  <td>
                    {updatedAt
                      && DateTime.fromSeconds(updatedAt).toLocaleString(DateTime.DATETIME_FULL)}
                  </td>
                </tr>
              </tbody>
            </Table>
          </Card>
        </Col>

        <Col xs={12} sm={6} md={8}>
          <Card className="mt-4 mb-4">
            <CardHeader>
              {propertyHeader}
            </CardHeader>
            <Table responsive>
              <thead>
                <tr>
                  <th scope="col">{tableTitleHeader}</th>
                  <th scope="col">{tableIdHeader}</th>
                  <th scope="col">
                    <abbr title={tableExternalIdentifierTitle}>{tableExternalIdentifierLabel}</abbr>
                  </th>
                  <th scope="col">{tableActiveStatusHeader}</th>
                </tr>
              </thead>
              <tbody>
                <tr key={`property-user-property-${propertyId}`}>
                  <th scope="row" colSpan={2}>
                    <Link to={`/admin/properties/${propertyId}`}>
                      {propertyTitle}
                    </Link>
                    <br />
                    <UUID uuid={propertyId} />
                  </th>
                  <td>
                    {propertyExternalIdentifier}
                  </td>
                  <td>
                    <BadgeActiveTimeRange active={propertyActive} />
                  </td>
                </tr>
              </tbody>
            </Table>
          </Card>

          <Card className="mt-4 mb-4">
            <CardHeader>
              {userHeader}
            </CardHeader>
            <Table responsive>
              <thead>
                <tr>
                  <th scope="col">{tableUsernameHeader}</th>
                  <th scope="col">{tableEmailHeader}</th>
                  <th scope="col">{tableIdHeader}</th>
                  <th scope="col">
                    <abbr title={tableExternalIdentifierTitle}>{tableExternalIdentifierLabel}</abbr>
                  </th>
                  <th scope="col">{tableActiveStatusHeader}</th>
                </tr>
              </thead>
              <tbody>
                <tr key={`property-user-user-${userId}`}>
                  <th scope="row" colSpan={2}>
                    <Link to={`/admin/users/${userId}`}>
                      {displayUserName(intl, user)}
                    </Link>
                    <br />
                    {userEmail}
                  </th>
                  <td colSpan={2}>
                    <UUID uuid={userId} />
                    <br />
                    {userExternalIdentifier}
                  </td>
                  <td>
                    <BadgeActiveTimeRange active={userActive} />
                  </td>
                </tr>
              </tbody>
            </Table>
          </Card>
        </Col>
      </Row>
    </>
  );
}

AdminPropertyUserShow.propTypes = {
  relay: PropTypes.shape({
    hasMore: PropTypes.func,
    isLoading: PropTypes.func,
    loadMore: PropTypes.func,
  }).isRequired,
  propertyUser: PropTypes.shape({
    uuid: PropTypes.string,
    externalIdentifier: PropTypes.string,
    createdAt: PropTypes.number,
    updatedAt: PropTypes.number,
    active: PropTypes.shape({
      start: PropTypes.number,
      finish: PropTypes.number,
    }),
    property: PropTypes.shape({
      uuid: PropTypes.string,
      title: PropTypes.string,
      externalIdentifier: PropTypes.string,
      active: PropTypes.shape({
        start: PropTypes.number,
        finish: PropTypes.number,
      }),
      address: PropTypes.shape({
        line1: PropTypes.string,
        line2: PropTypes.string,
        city: PropTypes.string,
        state: PropTypes.string,
        postcode: PropTypes.string,
      }),
    }),
    user: PropTypes.shape({
      uuid: PropTypes.string,
      externalIdentifier: PropTypes.string,
      email: PropTypes.string,
      givenName: PropTypes.string,
      familyName: PropTypes.string,
      active: PropTypes.shape({
        start: PropTypes.number,
        finish: PropTypes.number,
      }),
    }),
  }),
};

AdminPropertyUserShow.defaultProps = {
  propertyUser: null,
};

export default createFragmentContainer(
  AdminPropertyUserShow,
  {
    propertyUser: graphql`
      fragment AdminPropertyUserShow_propertyUser on PropertyUser {
        uuid
        externalIdentifier
        createdAt
        updatedAt
        active { start finish }
        property {
          uuid
          title
          externalIdentifier
          active { start finish }
        }
        user {
          uuid
          externalIdentifier
          email
          givenName
          familyName
          active { start finish }
        }
      }
    `,
  },
);
